import { toRouteData } from './payloadParser';

/**
 * Validates a notification's action against the brand allowlists served by the public config and
 * hands it to the site's router (`components/PushNotifications#handlePushNotification`), which
 * waits for the router and the session and knows every action type. A target that fails the
 * allowlist falls back to opening the site (no navigation) rather than to an unvalidated URL.
 */
const isAllowedUrl = (value, allowlist = []) => {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    return (
      url.protocol === 'https:' &&
      allowlist.some(h => host === h || host.endsWith(`.${h}`))
    );
  } catch {
    return false;
  }
};

const isAllowedScreen = (value, screens = []) => {
  if (!value) {
    return false;
  }
  // 'A|B|C' navigates A then B nested in it; the registry names the entry screen.
  const entry = String(value).split('|')[0];
  return (
    screens.length === 0 || screens.includes(entry) || screens.includes(value)
  );
};

/**
 * @returns {{ ok: boolean, reason?: string, data?: Object }} the route data or why it was refused
 */
export const validateAction = (notification, config = {}) => {
  const { action } = notification;

  if (!action || action.type === 'none') {
    return { ok: false, reason: 'no_action' };
  }
  if (!action.target) {
    return { ok: false, reason: 'no_target' };
  }
  if (
    action.type === 'url' &&
    !isAllowedUrl(action.target, config.url_allowlist || [])
  ) {
    return { ok: false, reason: 'url_not_allowed' };
  }
  if (
    action.type === 'screen' &&
    !isAllowedScreen(action.target, config.screens || [])
  ) {
    return { ok: false, reason: 'screen_not_allowed' };
  }
  return { ok: true, data: toRouteData(notification) };
};

/**
 * Routes the action through the existing handler. Returns what happened; never throws.
 */
export const route = async (notification, config, handlePushNotification) => {
  const check = validateAction(notification, config);

  if (!check.ok) {
    console.log('[NotificationsV2][router] not routing:', check.reason);
    return { routed: false, reason: check.reason };
  }

  try {
    const result = await handlePushNotification(check.data);
    return { routed: result !== false, reason: null };
  } catch (err) {
    console.log('[NotificationsV2][router] navigation failed', err?.message);
    return { routed: false, reason: 'navigation_failed' };
  }
};
